import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, LogOut, RefreshCw, Trash2, Mail, Phone, Calendar, MapPin, Inbox } from "lucide-react";
import { toast, Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rh_admin_token";

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState("");

  const fetchQuotes = useCallback(async (tok) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/quotes`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setQuotes(res.data || []);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
        toast.error("Sesjonen er utløpt — logg inn på nytt");
      } else {
        toast.error("Kunne ikke laste forespørsler");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchQuotes(token);
  }, [token, fetchQuotes]);

  const login = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      const tok = res.data.token;
      sessionStorage.setItem(TOKEN_KEY, tok);
      setToken(tok);
      setPassword("");
      toast.success("Innlogget");
    } catch (e) {
      const msg = e.response?.data?.detail || "Innlogging feilet";
      toast.error(typeof msg === "string" ? msg : "Innlogging feilet");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setQuotes([]);
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Slette denne forespørselen?")) return;
    try {
      await axios.delete(`${API}/admin/quotes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuotes((q) => q.filter((x) => x.id !== id));
      toast.success("Slettet");
    } catch {
      toast.error("Kunne ikke slette");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center px-4" data-testid="admin-login-page">
        <Toaster position="top-center" toastOptions={{ style: { background: "#0A0A0A", color: "#FFF", border: "2px solid #E11D2E", borderRadius: 0 } }} />
        <form
          onSubmit={login}
          className="w-full max-w-md bg-white border-2 border-black p-8 brutal-shadow-red"
          data-testid="admin-login-form"
        >
          <div className="text-xs font-bold tracking-[0.22em] text-[#E11D2E]">ADMIN</div>
          <h1 className="font-display text-4xl mt-3">RASKT HJEM AS</h1>
          <p className="text-sm text-zinc-600 mt-2">Logg inn for å se skjemainnsendinger</p>

          <label className="block mt-8">
            <span className="block text-[11px] font-bold tracking-[0.22em] uppercase text-zinc-500 mb-2">
              Passord
            </span>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-3 py-3 border-2 border-black bg-white text-black outline-none focus:border-[#E11D2E]"
                style={{ borderRadius: 0 }}
                autoFocus
                data-testid="admin-password-input"
              />
            </div>
          </label>

          <button
            type="submit"
            disabled={loading}
            className="mt-6 w-full bg-[#E11D2E] text-white px-6 py-3 font-bold uppercase tracking-wider text-sm border-2 border-black brutal-shadow disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {loading ? "Logger inn..." : "Logg inn"}
          </button>

          <a
            href="/"
            className="mt-4 block text-center text-xs text-zinc-500 hover:text-[#E11D2E]"
            data-testid="admin-back-home"
          >
            ← Tilbake til nettsiden
          </a>
        </form>
      </div>
    );
  }

  const filtered = quotes.filter(
    (q) =>
      !filter ||
      q.name.toLowerCase().includes(filter.toLowerCase()) ||
      q.email.toLowerCase().includes(filter.toLowerCase()) ||
      q.phone.includes(filter) ||
      q.service_type.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#F4F4F5]" data-testid="admin-dashboard">
      <Toaster position="top-center" toastOptions={{ style: { background: "#0A0A0A", color: "#FFF", border: "2px solid #E11D2E", borderRadius: 0 } }} />

      <header className="bg-black text-white border-b-2 border-[#E11D2E]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.22em] text-[#E11D2E] font-bold">ADMIN PANEL</div>
            <div className="font-display text-2xl">RASKT HJEM AS</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchQuotes(token)}
              className="inline-flex items-center gap-2 bg-white text-black px-3 py-2 border-2 border-white text-xs font-bold hover:bg-[#E11D2E] hover:text-white hover:border-[#E11D2E]"
              data-testid="admin-refresh"
            >
              <RefreshCw size={14} /> OPPDATER
            </button>
            <a href="/" className="inline-flex items-center gap-2 bg-transparent text-white px-3 py-2 border-2 border-white text-xs font-bold hover:bg-white hover:text-black" data-testid="admin-go-site">
              NETTSIDE
            </a>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 bg-[#E11D2E] text-white px-3 py-2 border-2 border-[#E11D2E] text-xs font-bold hover:bg-white hover:text-black hover:border-white"
              data-testid="admin-logout"
            >
              <LogOut size={14} /> LOGG UT
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-4xl md:text-5xl">FORESPØRSLER</h1>
            <p className="text-sm text-zinc-600 mt-2">{quotes.length} totalt · {filtered.length} viser</p>
          </div>
          <div className="md:w-80">
            <input
              type="text"
              placeholder="Søk på navn, e-post, telefon, tjeneste..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full px-4 py-3 border-2 border-black bg-white outline-none focus:border-[#E11D2E]"
              style={{ borderRadius: 0 }}
              data-testid="admin-search"
            />
          </div>
        </div>

        {loading && quotes.length === 0 ? (
          <div className="bg-white border-2 border-black p-12 text-center" data-testid="admin-loading">
            <RefreshCw size={28} className="mx-auto animate-spin text-[#E11D2E]" />
            <p className="mt-3 text-sm text-zinc-600">Laster...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border-2 border-black p-12 text-center" data-testid="admin-empty">
            <Inbox size={36} className="mx-auto text-zinc-400" />
            <p className="mt-3 font-display text-2xl">Ingen forespørsler</p>
            <p className="text-sm text-zinc-600 mt-1">
              {filter ? "Ingen treff på søket" : "Innsendte skjemaer vises her"}
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filtered.map((q) => (
              <div
                key={q.id}
                className="bg-white border-2 border-black p-5 md:p-6"
                data-testid={`admin-quote-${q.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="font-display text-2xl md:text-3xl leading-tight">{q.name}</div>
                    <div className="mt-1 inline-flex items-center gap-2 bg-black text-white px-2 py-1 text-[10px] font-bold tracking-[0.18em] uppercase">
                      {q.service_type}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] tracking-[0.2em] text-zinc-500 uppercase">Mottatt</div>
                    <div className="text-sm font-semibold mt-1">
                      {new Date(q.created_at).toLocaleString("nb-NO", { dateStyle: "short", timeStyle: "short" })}
                    </div>
                    <div className={`mt-1 inline-block text-[10px] px-2 py-0.5 border ${q.email_sent ? "border-emerald-600 text-emerald-700" : "border-amber-600 text-amber-700"}`}>
                      {q.email_sent ? "E-POST SENDT" : "KUN LAGRET"}
                    </div>
                  </div>
                </div>

                <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                  <Info icon={Phone} label="Telefon" value={q.phone} href={`tel:${q.phone}`} />
                  <Info icon={Mail} label="E-post" value={q.email} href={`mailto:${q.email}`} />
                  <Info icon={Calendar} label="Flyttedato" value={q.moving_date || "—"} />
                  <Info icon={MapPin} label="Fra → Til" value={(q.from_address || "—") + " → " + (q.to_address || "—")} />
                </div>

                {q.message && (
                  <div className="mt-5 p-4 bg-zinc-50 border-l-4 border-[#E11D2E] text-sm leading-relaxed">
                    {q.message}
                  </div>
                )}

                <div className="mt-5 pt-4 border-t border-zinc-200 flex items-center justify-between">
                  <div className="text-[10px] text-zinc-400 font-mono">{q.id}</div>
                  <button
                    onClick={() => deleteQuote(q.id)}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-zinc-500 hover:text-[#E11D2E] uppercase tracking-wider"
                    data-testid={`admin-delete-${q.id}`}
                  >
                    <Trash2 size={14} /> Slett
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function Info({ icon: Icon, label, value, href }) {
  const inner = (
    <>
      <div className="text-[10px] tracking-[0.2em] text-zinc-500 uppercase flex items-center gap-1.5">
        <Icon size={12} /> {label}
      </div>
      <div className="mt-1 text-sm font-medium break-words">{value}</div>
    </>
  );
  return href ? (
    <a href={href} className="block hover:text-[#E11D2E]">{inner}</a>
  ) : (
    <div>{inner}</div>
  );
}
